import Styles from './ImageMenuModal.module.scss'

import { Modal } from 'almanac-ui/Modal'
import { ImageMenuProvider, useImageMenuContext } from './ImageMenuContext'
import { Controls } from './Controls/Controls'
import { UploadsRow } from './UploadsRow/UploadsRow'
import { PhotosRow } from './PhotosRow/PhotosRow'
import { VideosRow } from './VideosRow/VideosRow'
import { Uploads } from './Uploads/Uploads'
import { AiGeneration } from './AiGeneration/AiGeneration'
import { Link } from './Link/Link'
import { AiGenerationRow } from './AiGenerationRow/AiGenerationRow'
import { BlazeRecommendationsRow } from './BlazeRecommendationsRow/BlazeRecommendationsRow'
import { GifsRow } from './GifsRow/GifsRow'
import { Gifs } from './Gifs/Gifs'
import { Text } from 'blaze-ui/Text/Text'
import { pluralize } from 'utils'
import { Button } from 'blaze-ui/Button'
import { Photos } from './Photos/Photos'
import { Videos } from './Videos/Videos'
import { BrandKitRow } from './BrandKitRow/BrandKitRow'
import { BrandKitMedia } from './BrandKitMedia/BrandKitMedia'
import { BlazeRecommendations } from './BlazeRecommendations/BlazeRecommendations'
import { entityName } from '../utils'
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { ImageMenuProps } from '../Types'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const Actions = () => {
  const { selectedImages, setSelectedImages, onUse, maxCount, isConfirming, action } = useImageMenuContext()
  const multiSelect = maxCount > 1
  const entity = entityName(selectedImages)
  const entityText = pluralize(entity, selectedImages.length || maxCount)

  return (
    <div className={Styles.actions}>
      {selectedImages.length > 0 && (
        <>
          {multiSelect && <Text variant="smallList">{`${selectedImages.length} ${entityText} selected`}</Text>}
          <Button variant="tertiary" onPress={() => setSelectedImages([])} isDisabled={isConfirming} size="lg">
            Deselect {multiSelect ? 'All' : ''}
          </Button>
        </>
      )}
      <Button onPress={() => onUse(selectedImages)} isDisabled={isConfirming || selectedImages.length === 0} size="lg">
        {action} {entityText}
      </Button>
    </div>
  )
}

const MainMenu = ({ title, showSearchBar }: { title: string; showSearchBar?: boolean }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { provider, selectedImages } = useImageMenuContext()

  if (provider) return null

  return (
    <>
      <Modal.Header title={title} className={Styles.header} />
      <Modal.Content className={Styles.content} withoutFooter={isSystemRedesignEnabled}>
        <Controls showSearchBar={showSearchBar} />
        <BlazeRecommendationsRow />
        <AiGenerationRow />
        <BrandKitRow />
        <UploadsRow />
        <PhotosRow />
        <VideosRow />
        <GifsRow />
      </Modal.Content>
      {selectedImages.length > 0 && (
        <Modal.Footer>
          <Modal.FooterContent slot="right">
            <Actions />
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </>
  )
}

const ProviderMenu = () => {
  const { provider } = useImageMenuContext()

  if (!provider) return null

  switch (provider) {
    case 'uploads':
      return <Uploads />
    case 'ai':
      return <AiGeneration />
    case 'link':
      return <Link />
    case 'gifs':
      return <Gifs />
    case 'photos':
      return <Photos />
    case 'videos':
      return <Videos />
    case 'brandKits':
      return <BrandKitMedia />
    case 'blazeRecommendations':
      return <BlazeRecommendations />
    default:
      return null
  }
}

export const ImageMenuModal = ({
  onUse,
  hiddenProviders = [],
  maxCount,
  uploadMedia = false,
  onUploadProgress,
  onUploadStart,
  onUploadEnd,
  onPressOutside,
  accept,
  initialQuery,
  title,
  initialBrandKitId,
  showSearchBar = false,
  defaultProvider,
  createParams,
  action = 'Insert',
  closeOnUse = true,
}: ImageMenuProps) => {
  let modalTitle = title
  if (!modalTitle) {
    if (accept === ACCEPTED_IMAGE_MIME_TYPES_NO_SVG || accept === ACCEPTED_IMAGE_MIME_TYPES)
      modalTitle = `${action} Images`
    else if (accept === ACCEPTED_VIDEO_MIME_TYPES) modalTitle = `${action} Videos`
    else modalTitle = `${action} Images & Videos`
  }

  return (
    <ImageMenuProvider
      onUse={onUse}
      uploadMedia={uploadMedia}
      maxCount={maxCount}
      hiddenProviders={hiddenProviders}
      onUploadProgress={onUploadProgress}
      onUploadStart={onUploadStart}
      onUploadEnd={onUploadEnd}
      accept={accept}
      initialQuery={initialQuery}
      initialBrandKitId={initialBrandKitId}
      defaultProvider={defaultProvider}
      createParams={createParams}
      action={action}
      closeOnUse={closeOnUse}
    >
      <Modal.Root size="md" onPressOutside={onPressOutside} wrapperClassName="ignore-click-outside">
        <MainMenu title={modalTitle} showSearchBar={showSearchBar} />
        <ProviderMenu />
      </Modal.Root>
    </ImageMenuProvider>
  )
}
